import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { maskConta, maskPhone, onlyDigits } from "@/lib/format";

const Field = ({ label, error, children, testId }) => (
  <div>
    <label className="mb-2 block text-xs font-medium uppercase tracking-[0.16em] text-slate-400">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400" data-testid={`${testId}-error`}>{error}</p>}
  </div>
);

function validate(f) {
  const e = {};
  const nome = f.nome.trim().replace(/\s+/g, " ");
  if (nome.length < 5 || nome.split(" ").length < 2 || !/^[A-Za-zÀ-ÖØ-öø-ÿ' .-]+$/.test(nome)) e.nome = "Informe o nome completo do responsável.";
  const tel = onlyDigits(f.telefone);
  if (![10, 11].includes(tel.length) || tel[0] === "0" || (tel.length === 11 && tel[2] !== "9")) e.telefone = "Informe um telefone válido com DDD.";
  if (!f.horario) e.horario = "Selecione o melhor horário para contato.";
  if (!/^\d{4}$/.test(f.agencia)) e.agencia = "A agência deve conter exatamente 4 dígitos.";
  const conta = onlyDigits(f.conta);
  if (conta.length < 4 || conta.length > 13) e.conta = "Informe um número de conta válido (com dígito).";
  if (!f.consentimento) e.consentimento = "É necessário consentir com o tratamento dos dados.";
  return e;
}

const inputCls = "h-12 border-white/10 bg-black/30 text-slate-50 placeholder:text-slate-600 focus-visible:ring-[var(--brand)]";

export const ScheduleForm = ({ state, slots, onBack, onSubmit, busy }) => {
  const [f, setF] = useState({ nome: "", telefone: "", horario: "", agencia: "", conta: "", consentimento: false });
  const [errors, setErrors] = useState({});
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setErrors((p) => ({ ...p, [k]: undefined })); };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length === 0) onSubmit({ ...f, telefone: onlyDigits(f.telefone), conta: onlyDigits(f.conta) });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl" data-testid="schedule-form-step">
      <button onClick={onBack} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="schedule-back-btn">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <h1 className="font-display text-3xl text-slate-50 sm:text-4xl">Agendar resgate</h1>
      <form onSubmit={submit} noValidate className="glass-card mt-8 space-y-6 p-6 sm:p-8" data-testid="schedule-form">
        <div className="rounded-sm border px-4 py-3" style={{ borderColor: "color-mix(in srgb, var(--brand) 35%, transparent)" }}>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-400">{state.tipo === "PF" ? "CPF" : "CNPJ"} da solicitação · confira antes de continuar</p>
          {state.company?.razao_social && <p className="mt-1 text-base font-semibold text-slate-50" data-testid="schedule-razao-social">{state.company.razao_social}</p>}
          <p className="mt-1 font-mono-x text-lg text-slate-50" data-testid="schedule-cnpj">{state.cnpj_fmt}</p>
        </div>
        <Field label="Nome completo do responsável" error={errors.nome} testId="input-nome">
          <Input value={f.nome} maxLength={150} onChange={(e) => set("nome", e.target.value)} className={inputCls} placeholder="Ex.: Maria Souza Lima" data-testid="input-nome" />
        </Field>
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="Telefone com DDD" error={errors.telefone} testId="input-telefone">
            <Input inputMode="tel" value={f.telefone} onChange={(e) => set("telefone", maskPhone(e.target.value))} className={`${inputCls} font-mono-x`} placeholder="(11) 91234-5678" data-testid="input-telefone" />
          </Field>
          <Field label="Número da agência" error={errors.agencia} testId="input-agencia">
            <Input inputMode="numeric" value={f.agencia} maxLength={4} onChange={(e) => set("agencia", onlyDigits(e.target.value).slice(0, 4))} className={`${inputCls} font-mono-x`} placeholder="0000" data-testid="input-agencia" />
          </Field>
        </div>
        <Field label="Número da conta (com dígito)" error={errors.conta} testId="input-conta">
          <Input inputMode="numeric" value={f.conta} onChange={(e) => set("conta", maskConta(e.target.value))} className={`${inputCls} font-mono-x`} placeholder="00000-0" data-testid="input-conta" />
        </Field>
        <Field label="Melhor horário para contato" error={errors.horario} testId="input-horario">
          <Select value={f.horario} onValueChange={(v) => set("horario", v)}>
            <SelectTrigger className={inputCls} data-testid="input-horario"><SelectValue placeholder="Selecione um horário" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#13192B] text-slate-100">
              {slots.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`horario-option-${s.id}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <div>
          <label className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
            <Checkbox checked={f.consentimento} onCheckedChange={(v) => set("consentimento", v === true)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--brand)] data-[state=checked]:text-black" data-testid="checkbox-lgpd" />
            <span>
              Autorizo o tratamento dos meus dados para fins de contato sobre o agendamento, conforme a{" "}
              <Link to="/privacidade" target="_blank" className="underline underline-offset-4" style={{ color: "var(--brand)" }} data-testid="link-privacidade">política de privacidade</Link> (LGPD).
            </span>
          </label>
          {errors.consentimento && <p className="mt-1.5 text-xs text-red-400" data-testid="checkbox-lgpd-error">{errors.consentimento}</p>}
        </div>
        <p className="text-xs text-slate-500">Não solicitamos senhas, tokens ou dados de cartão.</p>
        <button type="submit" disabled={busy} className="btn-gold h-14 w-full" data-testid="schedule-submit-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Solicitar agendamento"}
        </button>
      </form>
    </motion.div>
  );
};
